import { loadMenuImages } from '@theme/seal-menu-images';

const featureSelector = '[data-seal-header-menu-feature]';
const shelfSelector = '[data-seal-header-menu-shelf]';
const trackSelector = '[data-seal-header-menu-shelf-track]';
const previousSelector = '[data-seal-header-menu-shelf-prev]';
const nextSelector = '[data-seal-header-menu-shelf-next]';
const submenuSelector = '.seal-header-feature-submenu';
const deferredContentSelector = '[data-seal-header-deferred-content]';
const deferredTemplateSelector = 'template[data-seal-header-deferred-template]';
const prepareEventName = 'seal:header-menu:prepare';

function hydrateDeferredHeaderContent(root) {
  if (!(root instanceof Element)) return;

  const deferredContents = [];
  if (root.matches(deferredContentSelector)) deferredContents.push(root);
  deferredContents.push(...root.querySelectorAll(deferredContentSelector));

  for (const deferredContent of deferredContents) {
    if (deferredContent.dataset.sealHeaderHydrated === 'true') continue;

    const template = deferredContent.querySelector(`:scope > ${deferredTemplateSelector}`);
    if (!(template instanceof HTMLTemplateElement)) continue;

    template.replaceWith(template.content.cloneNode(true));
    deferredContent.dataset.sealHeaderHydrated = 'true';
  }
  loadMenuImages(root);
}

function handleHeaderContentIntent(event) {
  if (!(event.target instanceof Element)) return;

  const desktopItem = event.target.closest('.menu-list__list-item');
  if (desktopItem instanceof Element) hydrateDeferredHeaderContent(desktopItem);
}

function handleDrawerContentClick(event) {
  if (!(event.target instanceof Element)) return;

  const summary = event.target.closest('header-drawer summary');
  const drawerDetails = summary?.closest('details');
  const drawer = summary?.closest('header-drawer');
  const rootDetails = drawer?.querySelector(':scope > details');

  if (!(drawerDetails instanceof HTMLDetailsElement) || drawerDetails === rootDetails) return;

  window.requestAnimationFrame(() => {
    if (drawerDetails.isConnected) hydrateDeferredHeaderContent(drawerDetails);
  });
}

function cssLengthToPixels(value, element, fallback = 0) {
  const trimmedValue = value.trim();
  const numericValue = Number.parseFloat(trimmedValue);

  if (Number.isNaN(numericValue)) return fallback;
  if (trimmedValue.endsWith('rem')) {
    return numericValue * Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  }
  if (trimmedValue.endsWith('em')) {
    return numericValue * Number.parseFloat(getComputedStyle(element).fontSize);
  }

  return numericValue;
}

function getCssPixelValue(element, propertyName, fallback = 0) {
  return cssLengthToPixels(getComputedStyle(element).getPropertyValue(propertyName), element, fallback);
}

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getHorizontalContentBounds(element, fallbackMargin = 16) {
  const viewportWidth = document.documentElement.clientWidth;
  const container = element.closest('.header__columns');

  if (container instanceof HTMLElement) {
    const containerRect = container.getBoundingClientRect();
    const containerStyle = getComputedStyle(container);
    const left = Math.max(
      0,
      containerRect.left + cssLengthToPixels(containerStyle.paddingLeft, container)
    );
    const right = Math.min(
      viewportWidth,
      containerRect.right - cssLengthToPixels(containerStyle.paddingRight, container)
    );

    if (right > left) return { left, right };
  }

  return {
    left: fallbackMargin,
    right: Math.max(fallbackMargin, viewportWidth - fallbackMargin),
  };
}

function positionFeatureMenu(feature) {
  const submenu = feature.closest(submenuSelector);
  const menuItem = submenu?.closest('.menu-list__list-item');
  const topLink = menuItem?.querySelector(':scope > .menu-list__link');

  if (!(submenu instanceof HTMLElement) || !(menuItem instanceof HTMLElement) || !(topLink instanceof HTMLElement)) {
    return;
  }

  submenu.dataset.sealHeaderMenuPlacement = 'right';
  submenu.style.setProperty('--seal-header-submenu-offset', '0px');
  submenu.style.removeProperty('--seal-header-product-shelf-width');

  const pageMargin = getCssPixelValue(document.documentElement, '--page-margin', 16);
  const grid = submenu.querySelector('.mega-menu__grid');
  const gap = grid instanceof HTMLElement ? cssLengthToPixels(getComputedStyle(grid).columnGap, grid, 24) : 24;
  const linkRect = topLink.getBoundingClientRect();
  const submenuRect = submenu.getBoundingClientRect();
  const featureRect = feature.getBoundingClientRect();
  const hasTextColumn = Boolean(submenu.querySelector('.mega-menu__list .mega-menu__column'));
  const isPreviewMenu = feature.matches('.seal-header-menu-preview-slot');
  const viewportWidth = window.innerWidth;
  const availableRight = viewportWidth - pageMargin - linkRect.left;
  const featureSideWidth = hasTextColumn ? featureRect.width + gap : 0;

  let placement = 'right';
  let offset = 0;

  if (isPreviewMenu) {
    const bounds = getHorizontalContentBounds(menuItem, pageMargin);
    const maxPreviewWidth = Math.max(0, bounds.right - bounds.left);

    if (featureRect.width > maxPreviewWidth) {
      submenu.style.setProperty('--seal-header-product-shelf-width', `${Math.round(maxPreviewWidth)}px`);
    }

    const updatedFeatureRect = feature.getBoundingClientRect();
    const parentCenter = linkRect.left + linkRect.width / 2;
    const desiredFeatureLeft = parentCenter - updatedFeatureRect.width / 2;
    const maxFeatureLeft = Math.max(bounds.left, bounds.right - updatedFeatureRect.width);
    const clampedFeatureLeft = clampNumber(desiredFeatureLeft, bounds.left, maxFeatureLeft);

    submenu.dataset.sealHeaderMenuPlacement = 'center';
    submenu.style.setProperty(
      '--seal-header-submenu-offset',
      `${Math.round(clampedFeatureLeft - updatedFeatureRect.left)}px`
    );
    return;
  }

  if (submenuRect.width > availableRight) {
    if (hasTextColumn && linkRect.left - featureSideWidth >= pageMargin) {
      placement = 'left';
      offset = -featureSideWidth;
    } else {
      offset = Math.min(0, viewportWidth - pageMargin - linkRect.left - submenuRect.width);
      offset = Math.max(offset, pageMargin - linkRect.left);
    }
  }

  const minLeft = pageMargin;
  const maxRight = viewportWidth - pageMargin;
  const intendedLeft = linkRect.left + offset;
  const intendedRight = intendedLeft + submenuRect.width;

  if (intendedRight > maxRight) {
    offset -= intendedRight - maxRight;
  }

  if (linkRect.left + offset < minLeft) {
    offset += minLeft - (linkRect.left + offset);
  }

  submenu.dataset.sealHeaderMenuPlacement = placement;
  submenu.style.setProperty('--seal-header-submenu-offset', `${Math.round(offset)}px`);
}

function getShelfButton(eventTarget) {
  if (!(eventTarget instanceof Element)) return null;

  return eventTarget.closest(`${previousSelector}, ${nextSelector}`);
}

function getShelfTrack(button) {
  const shelf = button.closest(shelfSelector);

  return shelf?.querySelector(trackSelector) ?? null;
}

function updateShelfControls(track) {
  const shelf = track.closest(shelfSelector);
  const previousButton = shelf?.querySelector(previousSelector);
  const nextButton = shelf?.querySelector(nextSelector);

  if (!(previousButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) return;

  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth - 1);

  previousButton.disabled = track.scrollLeft <= 1;
  nextButton.disabled = track.scrollLeft >= maxScrollLeft;
}

function scrollShelf(button) {
  const track = getShelfTrack(button);

  if (!(track instanceof HTMLElement)) return;

  const direction = button.matches(previousSelector) ? -1 : 1;
  const scrollDistance = Math.max(track.clientWidth * 0.85, 160);

  track.scrollBy({
    left: direction * scrollDistance,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });

  window.setTimeout(() => updateShelfControls(track), 250);
}

function syncFeatureMenu(feature) {
  const track = feature.querySelector(trackSelector);

  positionFeatureMenu(feature);

  if (track instanceof HTMLElement) {
    updateShelfControls(track);
  }
}

function handleFeatureMenuPrepare(event) {
  if (!(event instanceof CustomEvent) || !(event.detail?.submenu instanceof HTMLElement)) return;

  hydrateDeferredHeaderContent(event.detail.submenu);
  const feature = event.detail.submenu.querySelector(featureSelector);
  if (feature instanceof HTMLElement) syncFeatureMenu(feature);
}

document.addEventListener('click', (event) => {
  const button = getShelfButton(event.target);

  if (!(button instanceof HTMLButtonElement)) return;

  scrollShelf(button);
});

document.addEventListener(prepareEventName, handleFeatureMenuPrepare);
document.addEventListener('pointerover', handleHeaderContentIntent, { capture: true, passive: true });
document.addEventListener('touchstart', handleHeaderContentIntent, { capture: true, passive: true });
document.addEventListener('focusin', handleHeaderContentIntent, true);
document.addEventListener('click', handleDrawerContentClick, true);

document.addEventListener(
  'scroll',
  (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches(trackSelector)) return;

    updateShelfControls(event.target);
  },
  true
);

function updateActiveFeatureMenus() {
  document.querySelectorAll(`${submenuSelector}[data-active]`).forEach((submenu) => {
    const feature = submenu.querySelector(featureSelector);
    if (feature instanceof HTMLElement) syncFeatureMenu(feature);
  });
}

let featureMenuUpdateFrame;

function scheduleActiveFeatureMenuUpdate() {
  window.cancelAnimationFrame(featureMenuUpdateFrame);
  featureMenuUpdateFrame = window.requestAnimationFrame(() => {
    featureMenuUpdateFrame = undefined;
    updateActiveFeatureMenus();
  });
}

window.addEventListener('resize', scheduleActiveFeatureMenuUpdate);
window.addEventListener('pageshow', scheduleActiveFeatureMenuUpdate);
document.fonts?.ready.then(scheduleActiveFeatureMenuUpdate);
